import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { closeModal } from '../redux/aiAnalysis/aiAnalysisSlice'
import { ReactComponent as LampIcon } from "../assets/svg/lamp.svg";
import { ReactComponent as InfoIcon } from "../assets/svg/info.svg";
import loaderGif from '../assets/gifs/loader.gif'
import useLocale from '../locales';
import Tooltip from './Tooltip';

const AIAnalysisModal = () => {
    const dispatch = useDispatch()
    const { isModalOpen, isThinking, analysisResult } = useSelector((state) => state.aiAnalysis)
    const { textData } = useLocale()
    useEffect(() => {
        if (isModalOpen) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = 'auto'
        }

        return () => {
            document.body.style.overflow = 'auto'
        }
    }, [isModalOpen])

    if (!isModalOpen) return null;

    const getStatusColor = (status) => {
        switch (status) {
            case 'Negative':
                return 'text-[#A80132] bg-[#F7E6EB] border border-[#A80132]'
            case 'Positive':
                return 'text-[#44A91F] bg-[#E5F1E5] border border-[#44A91F]'
            case 'Neutral':
                return 'text-[#6592CB] bg-[#DBEBFF] border border-[#6592CB]'
            default:
                return 'text-gray-600 bg-gray-50 border border-gray-600'
        }
    }

    const handleClose = () => {
        dispatch(closeModal())
    }

    const handleBackdropClick = (e) => {
        // Close modal when clicking on the backdrop
        if (e.target === e.currentTarget) {
            handleClose()
        }
    }

    return (
        <div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm flex justify-center items-center z-50"
            onClick={handleBackdropClick}
        >
            <div className="bg-white rounded-2xl p-8 shadow-2xl w-full max-w-[500px] relative animate-fadeIn">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-b-[#DDE1E5] pb-3">
                    <h2 className="text-xl text-[#282828]">{textData.aiAnalysis.aiAnalysis}</h2>
                    <button
                        onClick={handleClose}
                        className="w-8 h-8 bg-[#DDE1E5] flex items-center justify-center rounded-full  hover:bg-gray-300 transition-colors"
                    >
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path d="M1 1L9 9M1 9L9 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="pt-6 min-h-[300px] flex items-center justify-center">
                    {isThinking ? (
                        <div className="text-center">
                            <img src={loaderGif} alt="loader" />
                            <div
                                className="text-[24px] font-medium mb-4"
                                style={{
                                    background: 'linear-gradient(88.83deg, #E56489 -2.77%, #AE1595 30.94%, #5230EE 64.9%, #6B55F0 92.76%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                }}
                            >
                                {textData.goalPlanner.thinking}
                            </div>
                        </div>
                    ) : analysisResult ? (
                        <div className="w-full space-y-4">
                            <div className="space-y-2 bg-[#F6F8FB] px-4 py-3 rounded-[12px]">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-base font-medium text-[#282828] font-lato-bold-700">
                                        {analysisResult.title}
                                    </h3>
                                    <Tooltip
                                        content={textData.aiAnalysis.ratingTooltip || "Overall rating of the analysis."}
                                        placement="bottom"
                                    >
                                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(analysisResult.rating)}`}>
                                            {textData[analysisResult.rating] || analysisResult.rating}
                                        </span>
                                    </Tooltip>
                                </div>

                                {/* Description */}
                                <div className="text-[#282828] text-sm leading-relaxed font-lato-regular-400" dangerouslySetInnerHTML={{ __html: analysisResult.summary }} />
                            </div>

                            <div className={`border-2 border-[#44A91F] bg-green-50 rounded-xl p-4 space-y-2`}>
                                <div className="flex items-center gap-2 h-8 bg-[#44A91F] w-fit rounded-full px-[10px]">
                                    <span className=" text-white text-xs font-semibold px-1 py-1 rounded flex items-center gap-1">
                                        <LampIcon /> {textData.goalPlanner.proTip}
                                    </span>
                                </div>
                                <div className="text-gray-800 text-sm leading-relaxed font-lato-regular-400" dangerouslySetInnerHTML={{ __html: analysisResult.pro_tip }} />
                            </div>

                            <div className="flex items-start gap-2 text-xs text-gray-500 px-3 py-2.5 bg-[#F6F8FB] rounded-[12px]">
                                <InfoIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                <p className="leading-[120%] text-gray-font text-sm">
                                    {textData.goalPlanner.disclaimer}
                                </p>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    )
}

export default AIAnalysisModal
